import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core'
import { ButtonModule } from 'primeng/button'
import { CommonModule } from '@angular/common'
import { AbstractControl, ControlContainer, FormControl, FormGroup, FormGroupDirective } from '@angular/forms'
import { Tooltip } from 'primeng/tooltip'
import { PermissionService } from '@app/core/auth/services/permission.service'
import { CategoryService } from '@app/core/auth/services/category.service'

@Component({
  selector: 'albert-input-wrapper',
  standalone: true,
  imports: [ButtonModule, CommonModule, Tooltip],
  templateUrl: './albert-input-wrapper.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective
    }
  ]
})
export class AlbertInputWrapper {
  public readonly disabled = input<boolean>(false)
  public readonly tooltipLabel = input<string>('')
  public readonly groupName = input<string>('')
  public readonly controlName = input<string>('value')
  public readonly actionsLayout = input<'column' | 'row'>('column')

  private readonly permissionService = inject(PermissionService)
  private readonly categoryService = inject(CategoryService)
  public readonly isEffectiveDisabled = computed(() => {
    if (this.disabled()) {
      return true
    }
    const cat = this.categoryService.activeCategory()
    return cat === null ? false : !this.permissionService.canEdit(cat)
  })
  private readonly parentGroupDir = inject(FormGroupDirective)
  public readonly control = computed(() => {
    const name = this.groupName()
    const groupOrControl = name ? this.parentGroupDir.form.get(name) : this.parentGroupDir.form

    if (groupOrControl instanceof FormControl) {
      return groupOrControl
    }

    return groupOrControl?.get(this.controlName())
  })

  public onClear(): void {
    const target = this.getClearTarget()
    // Marked dirty before the value changes: the autosave only saves a change it sees on an already dirty form,
    // so on a freshly loaded certificate a clear would otherwise not be saved.
    if (target instanceof FormGroup) {
      target.markAsDirty()
      target.markAsTouched()
      Object.values(target.controls).forEach(control => {
        control.markAsDirty()
        control.markAsTouched()
        control.setValue(null)
      })
      return
    }

    target?.markAsDirty()
    target?.markAsTouched()
    target?.setValue(null)
  }

  private getClearTarget(): AbstractControl | null {
    const name = this.groupName()
    if (!name) {
      return this.control() ?? null
    }

    const groupOrControl = this.parentGroupDir.form.get(name)
    if (groupOrControl instanceof FormGroup && !groupOrControl.get(this.controlName())) {
      return groupOrControl
    }

    return this.control() ?? groupOrControl
  }
}
